import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MdArrowBack } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import style from "./MentorAssessmentView.module.css";
import Loader from "../Loder/Loader";
import { baseurl } from "../Url";

function toReadableStrings(val) {
  if (val == null) return [];

  if (Array.isArray(val)) {
    return val
      .flatMap((v) => toReadableStrings(v))
      .map((s) => String(s).trim())
      .filter(Boolean);
  }

  if (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean"
  ) {
    const s = String(val).trim();
    return s ? [s] : [];
  }

  if (typeof val === "object") {
    // Common Mongo object id shape
    if (val.$oid) return [String(val.$oid).trim()].filter(Boolean);

    // Common answer payload keys
    const preferredKeys = [
      "answer",
      "answers",
      "value",
      "text",
      "label",
      "option",
      "name",
    ];
    for (const k of preferredKeys) {
      if (val[k] != null) return toReadableStrings(val[k]);
    }

    // Fallback: concatenate nested values
    return Object.values(val).flatMap((v) => toReadableStrings(v));
  }

  return [String(val).trim()].filter(Boolean);
}

/** Normalize one assessment item from API into display shape. */
function normalizeAssessmentItem(raw, idx) {
  const id = String(raw?.unique_id ?? raw?.id ?? `q-${idx}`);
  const questionHtml = raw?.question != null ? String(raw.question) : "";
  let options = [];
  if (Array.isArray(raw?.options)) {
    options = raw.options.map((o) => {
      if (typeof o === "string") return o;
      if (o && typeof o === "object") {
        return (
          o.text ??
          o.label ??
          o.value ??
          o.option ??
          o.name ??
          JSON.stringify(o)
        );
      }
      return String(o);
    });
  }
  const actual = raw?.actual_ans ?? raw?.actual_answer ?? raw?.answer;
  const correctValues = toReadableStrings(actual);

  const userPick =
    raw?.user_answer ??
    raw?.user_selected ??
    raw?.submitted_answer ??
    raw?.selected_answer ??
    raw?.learner_answer;

  const userValues = toReadableStrings(userPick);

  return {
    id,
    raw,
    questionHtml,
    options,
    type: raw?.type || "",
    answerIs: raw?.answer_is ? String(raw.answer_is) : "",
    correctValues,
    userValues,
  };
}

const MentorAssessmentView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const userId =
    state.user_id ||
    state.viewUserId ||
    localStorage.getItem("USERID") ||
    "";
  const mainTopicId = state.main_topic_id || "";
  const topicId = state.topic_id || "";
  const topicTitle = state.topicTitle || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [apiDescription, setApiDescription] = useState("");
  const [assessments, setAssessments] = useState([]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const normalizedList = useMemo(
    () => assessments.map((a, i) => normalizeAssessmentItem(a, i)),
    [assessments]
  );

  const current = normalizedList[currentIndex] || null;

  const fetchMentorAssessment = useCallback(async () => {
    if (!userId || !mainTopicId || !topicId) {
      setLoading(false);
      setError(
        "Missing user_id, main_topic_id, or topic_id. Open this page from a module topic."
      );
      setAssessments([]);
      return;
    }

    setLoading(true);
    setError("");
    setApiDescription("");

    try {
      const res = await fetch(`${baseurl}/assesssment/view/by/mentor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          main_topic_id: mainTopicId,
          topic_id: topicId,
        }),
      });
      const data = await res.json();

      if (!data?.status || !Array.isArray(data.data)) {
        setAssessments([]);
        setApiDescription(data?.description || "No assessment data.");
        setLoading(false);
        return;
      }

      setAssessments(data.data);
      setApiDescription(data?.description || "");
    } catch (e) {
      console.error("Mentor assessment fetch failed:", e);
      setError("Failed to load assessment.");
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, [userId, mainTopicId, topicId]);

  useEffect(() => {
    fetchMentorAssessment();
  }, [fetchMentorAssessment]);

  useEffect(() => {
    if (normalizedList.length && currentIndex >= normalizedList.length) {
      setCurrentIndex(0);
    }
  }, [normalizedList.length, currentIndex]);

  const isOptionCorrect = (opt) => {
    if (!current) return false;
    const t = String(opt).trim();
    return current.correctValues.some((c) => c === t);
  };

  const isOptionUser = (opt) => {
    if (!current) return false;
    const t = String(opt).trim();
    return current.userValues.some((u) => u === t);
  };

  const shouldShowAnswerSummary = (item) => {
    if (!item) return false;
    const type = String(item.type || "").toLowerCase().trim();
    if (type === "multiple choices") return false;
    if (type === "ordinary") return true;
    return String(item.answerIs || "").toLowerCase().trim() === "incorrect";
  };

  return (
    <div className={style.container}>
      <div className={style.header}>
        <div className={style.headerLeft}>
          <button
            type="button"
            className={style.backButton}
            aria-label="Back"
            onClick={() => navigate(-1)}
          >
            <MdArrowBack color="#fff" size={16} />
          </button>
          <div className={style.headerTitle}>
            <h1>Mentor Assessment View</h1>
            <p className={style.breadcrumb}>
              {topicTitle
                ? `Topic: ${topicTitle}`
                : "Assessment / Question Review"}
            </p>
          </div>
        </div>
      </div>

      {loading ? <Loader /> : null}

      {!loading && error ? (
        <div className={style.noData} style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      ) : null}

      {!loading && !error && normalizedList.length === 0 ? (
        <div className={style.noData} style={{ marginBottom: "1rem" }}>
          {apiDescription || "No completed assessment found for this topic."}
        </div>
      ) : null}

      {!loading && !error && normalizedList.length > 0 ? (
        <div className={style.assessmentSection}>
        <div className={style.bodyTop}>
          <div className={style.bodyLeft}>
            <div className={style.progressSection}>
              <div className={style.topQuestion}>
                <div className={style.questionTopText}>
                  <h2>
                    Question{" "}
                    {normalizedList.length ? currentIndex + 1 : 0}
                    {/* {normalizedList.length
                      ? ` of ${normalizedList.length}`
                      : ""} */}
                  </h2>
                  {/* {current?.type ? (
                    <p style={{ margin: 0, color: "#64748b" }}>{current.type}</p>
                  ) : null} */}
                </div>
              </div>
            </div>

            {current && (
              <>
                {/* <div className={style.questionCard}> */}
                  <div className={style.questionSection}>
                    <div className={style.question}>
                      <div className={style.questionLeft}>
                        <span>Q.</span>
                      </div>
                      <div className={style.questionRight}>
                        <div
                          className={style.questionHtml}
                          dangerouslySetInnerHTML={{
                            __html: current.questionHtml || "<p></p>",
                          }}
                        />
                      </div>
                    </div>
                  {/* </div> */}

                  {current.options.length > 0 ? (
                    <>
                      <div className={style.optionSection}>
                        {current.options.map((option, idx) => {
                          const correct = isOptionCorrect(option);
                          const user = isOptionUser(option);
                          const extra = [
                            correct ? style.selectedOption : "",
                            user && !correct ? style.userPick : "",
                          ]
                            .filter(Boolean)
                            .join(" ");
                          return (
                            <div
                              key={`${current.id}-${idx}`}
                              className={`${style.option} ${extra}`}
                            >
                              <span className={style.optionLabel}>
                                {String.fromCharCode(65 + idx)}.
                              </span>
                              <span>{option}</span>
                              {correct ? (
                                <span
                                  style={{
                                    marginLeft: "auto",
                                    fontSize: "0.75rem",
                                    color: "#16a34a",
                                    fontWeight: 700,
                                  }}
                                >
                                  Correct
                                </span>
                              ) : null}
                              {user && !correct ? (
                                <span
                                  style={{
                                    marginLeft: "auto",
                                    fontSize: "0.75rem",
                                    color: "#ea580c",
                                    fontWeight: 700,
                                  }}
                                >
                                  Learner
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>

                      {shouldShowAnswerSummary(current) &&
                        (current.userValues.length > 0 ||
                          current.correctValues.length > 0) && (
                        <div
                          style={{
                            padding: "0.75rem 1rem 0",
                            color: "#475569",
                            display: "grid",
                            gap: "0.25rem",
                            fontSize: "0.95rem",
                          }}
                        >
                          {current.userValues.length > 0 ? (
                            <div>
                              <strong>Learner Answer:</strong>{" "}
                              {current.userValues.join(", ")}
                            </div>
                          ) : null}
                          {current.correctValues.length > 0 ? (
                            <div>
                              <strong>Actual Answer:</strong>{" "}
                              {current.correctValues.join(", ")}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ padding: "0 1rem 1rem", color: "#64748b" }}>
                      {shouldShowAnswerSummary(current) ? (
                        <>
                          {current.userValues.length ? (
                            <p style={{ margin: "0 0 0.25rem" }}>
                              <strong>Learner Answer:</strong>{" "}
                              {current.userValues.join(", ")}
                            </p>
                          ) : null}
                          {current.correctValues.length ? (
                            <p style={{ margin: 0 }}>
                              <strong>Actual Answer:</strong>{" "}
                              {current.correctValues.join(", ")}
                            </p>
                          ) : (
                            <p style={{ margin: 0 }}>
                              No options/answers available for this item.
                            </p>
                          )}
                        </>
                      ) : (
                        <p style={{ margin: 0 }}>
                          No options/answers available for this item.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className={style.questionButtonSection}>
                  <button
                    type="button"
                    className={style.previousButton}
                    disabled={currentIndex === 0}
                    onClick={() =>
                      setCurrentIndex((idx) => Math.max(0, idx - 1))
                    }
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className={style.nextButton}
                    disabled={
                      currentIndex >= normalizedList.length - 1 ||
                      normalizedList.length === 0
                    }
                    onClick={() =>
                      setCurrentIndex((idx) =>
                        Math.min(normalizedList.length - 1, idx + 1)
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>

          <div className={style.bodyRight}>
            <div className={style.questionPalette}>
              <h4>Question Palette</h4>
              <div className={style.number}>
                {normalizedList.map((q, idx) => {
                  const isCurrent = idx === currentIndex;
                  const answered =
                    q.correctValues.length > 0 || q.userValues.length > 0;
                  const isIncorrect =
                    String(q.answerIs || "").toLowerCase().trim() === "incorrect";
                  return (
                    <button
                      key={q.id}
                      type="button"
                      className={`${style.numbering} ${isCurrent ? style.currentQuestion : ""} ${answered ? style.answeredQuestion : ""} ${isIncorrect ? style.incorrectQuestion : ""}`}
                      onClick={() => setCurrentIndex(idx)}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        </div>
      ) : null}
    </div>
  );
};

export default MentorAssessmentView;
